import { useState } from "react";
import { Modal } from "../shared/Modal";
import { Button } from "../shared/Button";
import { Spinner } from "../shared/Spinner";
import { useApiCall } from "../../lib/useToastError";
import { useResource } from "../../lib/useResource";
import { AdminOrgs, type Member } from "../../lib/apiTyped";
import { fmtTs } from "../../lib/format";
import type { AdminUserRow } from "./UserActionsMenu";

export function TransferOwnershipModal({
  user,
  onClose,
  onDone,
}: {
  user: AdminUserRow;
  onClose: () => void;
  onDone: () => void;
}) {
  const orgId = user.defaultOrgId ?? null;
  const { data, loading, error } = useResource(
    () => (orgId ? AdminOrgs.details(orgId) : Promise.resolve(null)),
    [orgId],
  );
  const [selected, setSelected] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const { run } = useApiCall();

  async function submit() {
    if (!orgId || !selected || busy) return;
    setBusy(true);
    const r = await run(() => AdminOrgs.transfer(orgId, selected), {
      successMessage: "Ownership transferred.",
    });
    setBusy(false);
    if (r) onDone();
  }

  const currentOwnerUid =
    data?.org.ownerUid ?? data?.org.ownerId ?? null;
  const candidates: Member[] = (data?.members ?? []).filter(
    (m) => m.uid !== currentOwnerUid,
  );
  const isUserCurrentOwner = currentOwnerUid === user.uid;

  return (
    <Modal onClose={onClose} maxWidth="30rem">
      <h2 className="text-xl font-black mb-2 pr-6 text-ink">Transfer ownership</h2>
      {loading ? (
        <div className="py-10 flex justify-center">
          <Spinner />
        </div>
      ) : error ? (
        <p className="text-sm" style={{ color: "#C61418" }}>
          {error.message}
        </p>
      ) : !data ? null : !isUserCurrentOwner ? (
        <p className="text-sm text-ink-3">
          <strong>{user.email}</strong> is not the current owner of{" "}
          <span className="font-mono">{user.defaultOrgId}</span>. Nothing to
          transfer.
        </p>
      ) : candidates.length === 0 ? (
        <p className="text-sm text-ink-3">
          The organization has no other members to transfer ownership to.
          Have the current owner invite another member first.
        </p>
      ) : (
        <>
          <p className="text-sm text-ink-3 mb-4">
            You are about to remove <strong>{user.email}</strong> as owner of{" "}
            <strong>{data.org.name}</strong>. Choose who inherits — the previous
            owner becomes a regular member.
          </p>
          <div
            className="rounded-xl overflow-hidden"
            style={{ border: "1px solid rgba(0,0,0,0.10)" }}
          >
            {candidates.map((m, i) => {
              const active = selected === m.uid;
              return (
                <button
                  key={m.uid}
                  onClick={() => setSelected(m.uid)}
                  className="w-full text-left px-4 py-3 flex items-center justify-between transition-colors"
                  style={{
                    backgroundColor: active ? "rgba(0,0,0,0.05)" : "transparent",
                    borderTop: i > 0 ? "1px solid rgba(0,0,0,0.06)" : undefined,
                  }}
                >
                  <div className="min-w-0">
                    <p className="text-sm font-semibold truncate text-ink">
                      {m.email ?? m.uid}
                    </p>
                    <p className="text-xs text-ink-3 mt-0.5">
                      Joined {fmtTs(m.addedAt)} · role {m.role}
                    </p>
                  </div>
                  <div
                    className="w-4 h-4 rounded-full"
                    style={{
                      border: "2px solid #111111",
                      backgroundColor: active ? "#111111" : "transparent",
                    }}
                  />
                </button>
              );
            })}
          </div>
          <div className="mt-6 flex gap-3">
            <Button
              variant="primary"
              fullWidth
              onClick={submit}
              disabled={!selected || busy}
            >
              {busy ? "Transferring…" : "Confirm transfer"}
            </Button>
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
}
